import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { Button, Input } from 'reactstrap';
import { MdLabelOutline, MdWarningAmber } from 'react-icons/md';
import { setBeschriftung, removeBeschriftung, verwerfeAbweichendenScan } from '../../store/punkteMeldung/actions';
import { getBeschriftung } from '../../store/punkteMeldung/commonUtils';

const KEY_ENTER = 'Enter';
const KEY_ESCAPE = 'Escape';

/**
 * Beschriftung zu einer EFN - anklickbarer Wert, der sich in ein Eingabefeld verwandelt,
 * im Muster von TeilnahmedatumAsSpan.
 *
 * Bewusst ohne jede Pruefung des Inhalts: hier soll "Dr. med. Max Mustermann" genauso
 * eingetragen werden koennen wie "Tisch 3, hinten links". Genau das sagt der Hinweis ueber
 * der Liste zu - die App prueft diese Angaben nicht und meldet sie nicht weiter.
 */
const BeschriftungAsSpan = (props) => {
	const { beschriftung, readOnly = false, setBeschriftung, removeBeschriftung, verwerfeAbweichendenScan } = props;
	const [inBearbeitung, setInBearbeitung] = useState(false);
	const [text, setText] = useState('');
	const textFeld = useRef(null);

	useEffect(() => {
		if(!inBearbeitung) return;
		const feld = textFeld.current;
		if(!feld) return;
		feld.focus();
		// Cursor ans Ende statt an den Anfang - man kommt hier zum Ergaenzen und Korrigieren.
		const ende = feld.value.length;
		try { feld.setSelectionRange(ende, ende); } catch(e) { /* manche Feldtypen koennen das nicht */ }
	}, [inBearbeitung]);

	const starteBearbeitung = () => {
		setText(beschriftung.text);
		setInBearbeitung(true);
	};
	const uebernehmen = () => {
		setInBearbeitung(false);
		// Nur schreiben, wenn sich wirklich etwas geaendert hat. Sonst wuerde blosses
		// Aufklappen und Wegklicken einen gemerkten abweichenden Scan mit abraeumen - der
		// Widerspruch verschwaende genau in dem Moment, in dem man ihn ansehen will.
		if(text === beschriftung.text) return;
		if(!text.trim()) removeBeschriftung();
		else setBeschriftung(text);
	};
	const verwerfen = () => setInBearbeitung(false);
	// Drei Wege aus einem Widerspruch, und keiner davon ist der offensichtlich richtige:
	// haeufig steht vorab eine Rollennotiz da ("Referent", "Tisch 3") und der Name kommt
	// erst beim Scannen an der Tuer - dann ist Ergaenzen richtig. Steht dort ein falscher
	// Name, ist Ersetzen richtig. Stimmt der alte, ist Verwerfen richtig.
	const scanText = () => (beschriftung.abweichenderScan || {}).text || '';
	const ergaenztText = () => [beschriftung.text, scanText()].filter(t => t).join(' – ');
	const ergaenzeScan = () => {
		setInBearbeitung(false);
		setBeschriftung(ergaenztText());
	};
	const ersetzeDurchScan = () => {
		setInBearbeitung(false);
		setBeschriftung(scanText());
	};
	const behalteVorhandenes = () => {
		setInBearbeitung(false);
		verwerfeAbweichendenScan();
	};

	// iOS Safari fokussiert einen <button> beim Antippen nicht. Ohne das hier liefe: mousedown,
	// dann blur mit relatedTarget null, aufVerlassen schliesst die Bearbeitung - und der Knopf
	// ist aus dem DOM, bevor sein click ankommt. Auf dem iPad passierte deshalb gar nichts,
	// waehrend es unter Chrome/Edge funktionierte, weil die den Knopf fokussieren.
	// Im iPad-Simulator nachgemessen: mousedown kommt vor dem blur, preventDefault haelt den
	// Fokus im Feld, der click feuert. Betrifft genauso Safari und Firefox auf dem Mac.
	const haltenFokus = (e) => e.preventDefault();

	const aufTaste = (e) => {
		if(e.key === KEY_ENTER) uebernehmen();
		if(e.key === KEY_ESCAPE) verwerfen();
	};
	// Die Knoepfe zum Aufloesen eines Widerspruchs stehen innerhalb dieses Bereichs - der Sprung
	// dorthin ist kein Verlassen der Bearbeitung.
	const aufVerlassen = (e) => {
		if(e.currentTarget.contains(e.relatedTarget)) return;
		uebernehmen();
	};

	const abweichenderScan = beschriftung.abweichenderScan;
	const abweichenderScanText = abweichenderScan ? (abweichenderScan.text || '') : '';

	if(inBearbeitung) return (
		<div onBlur={aufVerlassen}>
			<Input innerRef={textFeld} bsSize="sm" type="text" value={text} placeholder="Name / Notiz"
				onChange={(e) => setText(e.target.value)} onKeyUp={aufTaste} />
			{/* Hier wird der Widerspruch aufgeloest, nicht am Einlass. Beide Wege sind eine
			    bewusste Entscheidung - deshalb steht der gescannte Name im Klartext da. */}
			{ abweichenderScan &&
				<div className="small mt-1">
					<span className="text-warning-emphasis">Ein Scan nannte hier: „{abweichenderScanText}“</span>
					{/* Jeder Knopf entscheidet sofort und schliesst - sonst waere es zweistufig:
					    erst das Feld fuellen, dann noch bestaetigen. Der Titel zeigt vorab, was
					    danach dasteht; "ersetzen" heisst bewusst nicht "übernehmen", weil dabei
					    etwas verloren geht. Verlustfrei steht vorn. */}
					<Button color="link" size="sm" className="px-1 py-0"
						onMouseDown={haltenFokus} onClick={ergaenzeScan}
						title={`Ergebnis: „${ergaenztText()}“`}>ergänzen</Button>
					<Button color="link" size="sm" className="px-1 py-0"
						onMouseDown={haltenFokus} onClick={ersetzeDurchScan}
						title={`Ergebnis: „${scanText()}“`}>ersetzen</Button>
					<Button color="link" size="sm" className="px-1 py-0 text-body-secondary"
						onMouseDown={haltenFokus} onClick={behalteVorhandenes}
						title={`Ergebnis: „${beschriftung.text}“`}>verwerfen</Button>
				</div>
			}
		</div>
	);

	const titel = beschriftung.text || '';
	const warnTitel = `Ein Scan nannte hier: „${abweichenderScanText}“`;
	const Widerspruch = () => abweichenderScan
		? <MdWarningAmber className="ms-1 text-warning-emphasis" size="1.1em" title={warnTitel} />
		: null;
	if(readOnly) return (
		<span title={titel}>
			{beschriftung.text
				? <><span>{beschriftung.text}</span><Widerspruch /></>
				: null}
		</span>
	);

	return (
		<span>
			{ beschriftung.text
				? <span className="clickableValue" title={abweichenderScan ? warnTitel : titel}
						onClick={starteBearbeitung}>
						{beschriftung.text}<Widerspruch />
					</span>
				: <span className="clickableValue text-body-secondary small" title="Name oder Notiz hinterlegen – bleibt in diesem Browser"
						onClick={starteBearbeitung}>
						<MdLabelOutline className="me-1" size="1.15em" />Name / Notiz
					</span>
			}
		</span>
	);
};

const mapStateToProps = (state, ownProps) => ({
	beschriftung: getBeschriftung(state, ownProps.vnr, ownProps.efn)
});

const mapDispatchToProp = (dispatch, ownProps) => ({
	setBeschriftung: (text) => dispatch(setBeschriftung(ownProps.vnr, ownProps.efn, text)),
	removeBeschriftung: () => dispatch(removeBeschriftung(ownProps.vnr, ownProps.efn)),
	verwerfeAbweichendenScan: () => dispatch(verwerfeAbweichendenScan(ownProps.vnr, ownProps.efn))
});

export default connect(mapStateToProps, mapDispatchToProp)(BeschriftungAsSpan);
